import {
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { PageHeaderContainer } from 'src/components/PageHeader/PageHeaderContainer';
import { Search } from 'src/components/Search/Search';
import { Iconify } from 'src/components/iconify';
import { ChannelAvatar } from 'src/features/channel/ChannelAvatar';
import { WorkspaceShareMembersAvatar } from 'src/features/workspace/WorkspaceShareMembersAvatar';
import { HeaderIcon } from 'src/layouts/DashboardLayout/shared/styles';
import { useLocales } from 'src/locales';
import { useChatContext } from 'src/provider/ChatContext';
import { useChatConversationContext } from 'src/sections/@dashboard/chat/ChatConversation/useChatConversationContext';
import { ChannelThreads } from 'src/sections/@dashboard/chat/ChatHeader/ChannelThreads/ChannelThreads';
import {
  ChatDetailsTitle,
  useChatDetailsDialog,
} from 'src/sections/@dashboard/chat/shared/ChatOverviewDialog';
import { PinnedMessage } from './PinnedMessages';
import { ChatHeaderProps } from './types';

const StyledTitle = styled(Typography)(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ChatHeaderGroup = ({ conversation, onClose }: ChatHeaderProps) => {
  const [hideButtons, setHideButtons] = useState(false);
  const { conversationKey = '' } = useParams();
  const { translate } = useLocales();
  const navigate = useNavigate();

  const { searchTerm, setSearchTerm } = useChatConversationContext();

  const { showChatDetailsDialog } = useChatDetailsDialog();

  const { isDrawer, isCompact, setCurrentActiveChat } = useChatContext();

  const shouldRenderButtons = conversation._id !== conversationKey;

  const handleShowDetails = (tab?: ChatDetailsTitle) => {
    showChatDetailsDialog({ conversationId: conversation._id, tab });
  };

  const goToChannel = () => {
    navigate(PATH_DASHBOARD.chat.view(conversation._id));
    if (onClose) onClose();
  };

  const handleClose = () => {
    if (onClose) onClose();
    setCurrentActiveChat('');
  };

  return (
    <PageHeaderContainer>
      <Stack
        direction="row"
        alignItems="center"
        overflow="hidden"
        gap={1}
        onClick={() => handleShowDetails()}
        sx={{ cursor: 'pointer' }}
      >
        <HeaderIcon>
          <ChannelAvatar key={conversation._id} channel={conversation} />
        </HeaderIcon>
        <StyledTitle variant="h4">{conversation.name}</StyledTitle>
      </Stack>
      {conversation?._id && (
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!hideButtons && (
              <>
                {!conversation.parentId && (
                  <ChannelThreads
                    channelId={conversation._id}
                    compact={isCompact}
                  />
                )}
                <PinnedMessage
                  channelId={conversation._id}
                  compact={isCompact}
                />
              </>
            )}
            <Search
              searchTerm={searchTerm}
              onSearch={(inputValue) => setSearchTerm(inputValue)}
              clearSearch={() => setSearchTerm('')}
              onShowInput={(visibility) => setHideButtons(visibility)}
              onlyIcon={isCompact}
            />
          </Box>
          <Divider orientation="vertical" flexItem />
          <Stack
            onClick={() => handleShowDetails(ChatDetailsTitle.MEMBERS)}
            style={{ cursor: 'pointer' }}
          >
            <WorkspaceShareMembersAvatar
              members={conversation.participants}
              size="small"
              compact={isCompact}
            />
          </Stack>
          {isDrawer && shouldRenderButtons && (
            <>
              <Divider orientation="vertical" flexItem sx={{ ml: 1 }} />

              <Tooltip title={translate('communicate.open_chat')}>
                <IconButton onClick={goToChannel}>
                  <Iconify icon="tabler:message-2-share" />
                </IconButton>
              </Tooltip>

              <IconButton onClick={handleClose}>
                <Iconify icon="tabler:x" />
              </IconButton>
            </>
          )}
        </Stack>
      )}
    </PageHeaderContainer>
  );
};
